import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useContext
} from "react";
import PropTypes from "prop-types";
import { Trans, t, Plural } from "@lingui/macro";
import shallow from "zustand/shallow";
import { i18nMark } from "@lingui/react";
import { useHistory } from "react-router-dom";

import { ErrorBoundary } from "widgets/layouts";
import {
  UserIcon,
  AVATAR_SIZE,
  StatusChip,
  STATUS_CHIP_COLOR,
  STATUS_CHIP_TYPE,
  CHIP_SIZES,
  Icon,
  Collage
} from "widgets/datadisplay";
import { i18n } from "base/i18n";
import { ReadonlyEditor, FORMAT_TYPES } from "widgets/richtext";
import {
  Tooltip,
  TOOLTIP_TYPES,
  TOOLTIP_POSITION,
  Toast,
  BannerNotification,
  Skeleton
} from "widgets/feedback";
import { hasAccess } from "widgets/others";
import { getFormattedDate } from "base/utils";
import { Classes } from "base/classes";
import { PRODUCT_TYPES } from "base/constants";

import { Embedurl } from "../post/embedurl";
import {
  getTopicContents,
  onUpdateBookmark,
  updateTopicPin
} from "@/common/utils/utils";
import { useForumStore } from "@/common/store/forum.store";
import { ForumService } from "@/common/service/forum.service";
import { useChannelStore } from "@/common/store/channel.store";
import { PostViewsModal } from "../reaction/post.views.modal";
import { TopicDetail } from "./topic.detail";
import {
  CHANNEL_ROLES,
  ERROR_CODE,
  DATE_FORMAT_LONG,
  CHANNEL_CONST
} from "@/common/utils/constant";
import { SpaceContext } from "@/container/forum";

import styles from "../../forum/forum.css";

import CommentIcon from "../../icons/Comments.svg";
import ViewIcon from "../../icons/Views.svg";
import PinnedIcon from "../../icons/Pinned.svg";
import PinIcon from "../../icons/Pin.svg";
import UpvoteIcon from "@/common/icons/Upvote.svg";
import DownvoteIcon from "@/common/icons/Downvote.svg";
import BookmarkIcon from "../../icons/BookMark.svg";
import BookmarkedIcon from "../../icons/BookMarkFilled.svg";
import ClosedIcon from "@/common/icons/ClosedTopic.svg";
import SolvedIcon from "@/common/icons/Solved.svg";

const PIN_TOPIC = {
  true: i18nMark("Unpin this topic"),
  false: i18nMark("Pin this topic")
};

const { showSuccess } = Toast;
export function TopicCard({
  topicId,
  mediumId,
  isPinned = false,
  totalComments,
  teamId,
  activeTabId,
  fromNotification,
  currentTopicId,
  fromHome,
  notificationData = [],
  readOnly,
  isFromSearch,
  showVotes = true,
  showComments = true,
  showPin = true,
  updateBookmarkList,
  isFromBookmarks,
  bookmarkContent,
  forumName,
  onCardClick,
  isVerticalSplit,
  removeBookMark
}) {
  let history = useHistory();
  const { spaceRoute } = useContext(SpaceContext);
  const { Product } =
    window.accountDetails.UserDetails.AccountDetails.LicenseInfo;

  const topic = useForumStore(
    // eslint-disable-next-line kf/hook-func-name
    useCallback((state) => state.topicData[topicId], [topicId])
  );
  const { pinnedTopicIds } = useForumStore((state) => ({
    pinnedTopicIds: state.pinnedTopicIds
  }));
  const { roles } = useChannelStore(
    (state) => ({ roles: state.roles }),
    shallow
  );

  const [topicData, setTopicData] = useState(topic || {});
  const [isTopicLoading, setTopicLoading] = useState(false);
  const [isNoTopic, setNoTopic] = useState(false);
  const [voteCount, setVoteCount] = useState(0);
  const [showViewModal, setShowViewModal] = useState(false);
  const [totalCommentsCount, setTotalCommentsCount] = useState(0);
  const [openTopic, setOpenTopic] = useState(false);

  const [heading, topicContent, imageNode] = useMemo(
    function getContents() {
      return getTopicContents(topicData, true);
    },
    [topicData]
  );

  const headerText = heading[0]?.nodes[0].text;

  const isAdmin = hasAccess({
    roles: [CHANNEL_ROLES.ADMIN, CHANNEL_ROLES.MODERATOR],
    flowRoles: roles
  });

  const PinIconContainer = isAdmin ? Tooltip : "div";

  useEffect(
    function setTopicDataHandler() {
      setTopicData(topic || {});
      setNoTopic(topic?._is_deleted);
    },
    [topic]
  );

  useEffect(
    function setTotalCount() {
      if (totalComments) setTotalCommentsCount(totalComments);
    },
    [totalComments]
  );

  useEffect(
    function setTopic() {
      if (!topic && !isFromBookmarks) {
        setTopicLoading(true);
        const [getTopicPromise] = ForumService.getTopicDetails(
          teamId,
          mediumId,
          topicId
        );
        getTopicPromise()
          .then((result) => {
            useForumStore.getState().setTopicData(result);
            const [commentBatch] = ForumService.getCommentBatch(
              teamId,
              mediumId,
              {
                post_ids: [topicId]
              }
            );
            commentBatch().then((result) => {
              useForumStore.getState().setBatchTopicDetails(result);
              setTopicLoading(false);
            });
          })
          .catch((err) => {
            setTopicLoading(false);
            if (
              err.error_code === ERROR_CODE.KISSFLOW_ERROR_23002 ||
              err.error_code === ERROR_CODE.KISSFLOW_ERROR_060103
            ) {
              setNoTopic(true);
            }
            console.info(err);
          });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [topic]
  );

  useEffect(
    function setTopicCommentCount() {
      if (isFromBookmarks) {
        setTopicData(bookmarkContent || {});
        const [commentBatch] = ForumService.getCommentBatch(teamId, mediumId, {
          post_ids: [bookmarkContent._id]
        });
        commentBatch().then((result) => {
          setTotalCommentsCount(result[bookmarkContent._id]?.TotalComments);
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isFromBookmarks]
  );

  const isPinnedTopic = useMemo(
    function isPinned() {
      return pinnedTopicIds.includes(topicData._id);
    },
    [pinnedTopicIds, topicData._id]
  );

  const detailPageRedirection = useMemo(
    function redirection() {
      let redirection;
      if (fromNotification) {
        redirection = "/notification";
      } else if (fromHome) {
        redirection = "/home";
      }
      return redirection;
    },
    [fromNotification, fromHome]
  );

  useEffect(
    function setCount() {
      setVoteCount(topicData.VoteCount || 0);
    },
    [topicData.VoteCount]
  );

  function getPlugins(topicData) {
    return {
      [FORMAT_TYPES.EMBED_LINK]: (prop) => {
        return (
          <Embedurl
            innerRef={{}}
            node={prop.node.data}
            postId={topicData?._id}
            readOnly={true}
          />
        );
      },
      [FORMAT_TYPES.COLLAGE]: (prop) => {
        return <Collage innerRef={{}} node={prop.node.data} readOnly={true} />;
      }
    };
  }

  function getBookMarkIcon(isVertical = false) {
    return (
      <div className={styles.bookMarkIcon}>
        {isVertical && (
          <>
            {topicData.IsSolved && (
              <Tooltip content={i18n._(t`Solved`)}>
                <SolvedIcon />
              </Tooltip>
            )}
            {topicData.IsClosed && (
              <Tooltip content={i18n._(t`Closed`)}>
                <ClosedIcon />
              </Tooltip>
            )}
          </>
        )}
        <Tooltip
          content={
            topicData.IsBookmarked
              ? i18n._(t`Remove bookmark`)
              : i18n._(t`Bookmark this topic`)
          }
        >
          <div
            onClick={(e) => {
              onUpdateBookmark({
                e,
                teamId,
                mediumId,
                instanceId: topicData._id,
                isAdd: !topicData.IsBookmarked,
                isPost: false,
                updateBookmark: updateBookmarkList,
                showSuccess: topicData.IsBookmarked
                  ? showRemoveSuccess
                  : showAddSuccess
              });
              removeBookMark(topicId);
            }}
          >
            {topicData.IsBookmarked ? (
              <BookmarkedIcon />
            ) : (
              <div className={styles.topicbookmark}>
                <BookmarkIcon width={20} height={20} />
              </div>
            )}
          </div>
        </Tooltip>
      </div>
    );
  }

  function pinTopic(event) {
    event.stopPropagation();
    updateTopicPin({
      topicDataId: topicData._id,
      isPinnedTopic,
      teamId,
      mediumId,
      activeTabId
    });
  }

  function onClickCard() {
    if (readOnly) return;
    if (fromNotification || isFromBookmarks || isFromSearch) {
      history.push({
        pathname: `/${
          spaceRoute || "space"
        }/${teamId}/channel/${mediumId}/topic/${topicId}`,
        state: isFromBookmarks
          ? {}
          : { notificationData, redirect: detailPageRedirection }
      });
    } else {
      onCardClick();
    }
  }

  function showAddSuccess() {
    showSuccess({ message: i18n._(t`The topic was added to your bookmarks`) });
  }
  function showRemoveSuccess() {
    showSuccess({
      message: i18n._(t`The topic was removed from your bookmarks`)
    });
  }

  function onClickChannelName(e) {
    e.stopPropagation();
    history.push(`/${spaceRoute}/${teamId}/channel/${mediumId}`);
  }

  if (!topicData || !topicData._id) return null;

  return (
    <ErrorBoundary>
      {isNoTopic ? (
        <div
          className={`${styles.banner} ${
            fromNotification ? styles.notification : ""
          }`}
        >
          <BannerNotification>
            <Trans>This topic has been deleted</Trans>
          </BannerNotification>
        </div>
      ) : (
        <div
          className={`${styles.topicCard} ${
            showPin && isPinnedTopic ? styles.pinnedTopic : ""
          } ${
            fromNotification || isFromSearch || isFromBookmarks
              ? styles.notificationCard
              : ""
          } ${
            isVerticalSplit && currentTopicId === topicId ? styles.active : ""
          }`}
          onClick={onClickCard}
        >
          {showPin && (
            <div
              onClick={isAdmin && pinTopic}
              className={`${styles.df8} ${styles.topicPinIcon} ${
                !isAdmin ? styles.grayFill : ""
              } ${isPinnedTopic ? styles.pinned : ""}`}
            >
              <Icon paddingNone={true} highlight={false}>
                <PinIconContainer
                  content={i18n._(PIN_TOPIC[isPinnedTopic || false])}
                >
                  {isPinnedTopic ? (
                    <PinnedIcon className={styles.ml16} />
                  ) : (
                    isAdmin && (
                      <div className={styles.topicpin}>
                        <PinIcon className={styles.ml16} />
                      </div>
                    )
                  )}
                </PinIconContainer>
              </Icon>
            </div>
          )}
          {isVerticalSplit &&
            Product !== PRODUCT_TYPES.KF_COMMUNITY &&
            getBookMarkIcon(true)}
          <div className={styles.flexBox}>
            <div className={styles.left}>
              <UserIcon
                size={isVerticalSplit ? AVATAR_SIZE.SIZE24 : AVATAR_SIZE.SIZE36}
                userId={topicData._created_by._id}
                name={topicData._created_by.Name}
              />
              <div className={styles.topicCardInfo}>
                <Trans>
                  <span
                    className={`base ${forumName && "text-link"} ${
                      Classes.ellipsis
                    } ${styles.creatorname}`}
                  >
                    {topicData._created_by.Name}
                  </span>
                  {forumName && (
                    <React.Fragment>
                      {" "}
                      <span>created a topic in</span>{" "}
                      <span
                        className={"text-link"}
                        onClick={onClickChannelName}
                      >
                        {forumName}
                      </span>
                    </React.Fragment>
                  )}
                </Trans>
                <div className={Classes.tiny400}>
                  {" "}
                  {getFormattedDate(topicData._created_at, DATE_FORMAT_LONG)}
                </div>
              </div>
            </div>
            {!fromHome && !isVerticalSplit && (
              <div className={styles.right}>
                {topicData.IsSolved && (
                  <StatusChip
                    size={CHIP_SIZES.MEDIUM}
                    color={STATUS_CHIP_COLOR.ORANGE}
                    type={STATUS_CHIP_TYPE.LITE}
                  >
                    <Trans>Solved</Trans>
                  </StatusChip>
                )}
                {topicData.IsClosed && (
                  <div className={isVerticalSplit ? styles.ml6 : ""}>
                    <StatusChip
                      size={CHIP_SIZES.MEDIUM}
                      color={STATUS_CHIP_COLOR.GRAY}
                      type={STATUS_CHIP_TYPE.LITE}
                    >
                      <Trans>Closed</Trans>
                    </StatusChip>
                  </div>
                )}
              </div>
            )}
          </div>
          <Skeleton
            active={true}
            loading={isTopicLoading}
            paragraph={{ rows: 2, width: ["70%, 50%"] }}
            title={false}
            className={styles.topicSkeleton}
          >
            <div
              className={styles.topicSection}
              onClickCapture={(e) => {
                onClickCard();
                e.stopPropagation();
              }}
            >
              {imageNode.length > 0 && !isVerticalSplit && (
                <Collage
                  innerRef={{}}
                  node={imageNode[0].data}
                  readOnly={true}
                  className={styles.imageNodeWrapper}
                />
              )}
              <div
                className={`${
                  isVerticalSplit ? Classes.h6600 : Classes.h4600
                } ${Classes.ellipsis2}`}
              >
                {headerText}
              </div>
              <ReadonlyEditor
                value={topicContent}
                renderer={getPlugins()}
                className={`${styles.topicText} ${Classes.base400} ${
                  Classes.ellipsis
                } ${
                  !headerText
                    ? `${Classes.ellipsis2} ${styles.noheadertext}`
                    : ""
                }`}
                id={`id-${topicData._id}`}
              />
            </div>
          </Skeleton>
          {!isVerticalSplit && (
            <div className={styles.flexBox}>
              <div className={`${styles.topicActions} ${styles.df8}`}>
                {showVotes && (
                  <>
                    <Icon paddingNone={true} highlight={false}>
                      <UpvoteIcon
                        className={`${styles.voteIcon} ${styles.disableIcon}`}
                      />
                    </Icon>
                    {voteCount || 0}
                    <Icon paddingNone={true} highlight={false}>
                      <DownvoteIcon
                        className={`${styles.voteIcon} ${styles.disableIcon}`}
                      />
                    </Icon>
                  </>
                )}
                {showComments && (
                  <>
                    <Tooltip
                      content={<Trans>Comment</Trans>}
                      type={TOOLTIP_TYPES.SMALL}
                      position={TOOLTIP_POSITION.TOP}
                    >
                      <Icon
                        paddingNone={true}
                        highlight={false}
                        className={styles.cardCommentIcon}
                      >
                        <CommentIcon className={`${styles.ml16}`} />
                      </Icon>
                    </Tooltip>
                    <div>{totalCommentsCount || 0}</div>
                  </>
                )}
                <Tooltip
                  content={<Trans>Views</Trans>}
                  type={TOOLTIP_TYPES.SMALL}
                  position={TOOLTIP_POSITION.TOP}
                >
                  <Icon
                    paddingNone={true}
                    highlight={false}
                    onClick={(e) => {
                      if (topicData.SeenCount) {
                        e.stopPropagation();
                        setShowViewModal(true);
                      }
                    }}
                    className={styles.cardCommentIcon}
                  >
                    <ViewIcon className={styles.ml16} />
                  </Icon>
                </Tooltip>
                <div>{topicData.SeenCount || 0}</div>
              </div>
              {Product !== PRODUCT_TYPES.KF_COMMUNITY && getBookMarkIcon()}
            </div>
          )}
          {showViewModal && (
            <PostViewsModal
              title={
                <Plural
                  value={topicData.SeenCount}
                  one="# member viewed this topic"
                  other="# members viewed this topic"
                />
              }
              onClose={() => setShowViewModal(false)}
              totalViews={topicData.SeenCount}
              medium={CHANNEL_CONST.CHANNEL}
              mediumId={mediumId}
              instanceId={topicData._id}
              teamId={teamId}
            />
          )}
        </div>
      )}
      {openTopic && (
        <TopicDetail
          teamId={teamId}
          listTopicId={topicId}
          closeModalHandler={() => setOpenTopic(false)}
          activeTabId={activeTabId}
          fromList
        />
      )}
    </ErrorBoundary>
  );
}

TopicCard.propTypes = {
  topic: PropTypes.object,
  getAllTopics: PropTypes.func,
  getAllPinnedTopics: PropTypes.func,
  isPinned: PropTypes.bool,
  topicId: PropTypes.string,
  mediumId: PropTypes.string,
  activeTabId: PropTypes.string,
  fromNotification: PropTypes.bool,
  fromHome: PropTypes.bool,
  totalComments: PropTypes.number,
  teamId: PropTypes.string,
  notificationData: PropTypes.array,
  readOnly: PropTypes.bool,
  isFromSearch: PropTypes.bool,
  showVotes: PropTypes.bool,
  showComments: PropTypes.bool,
  showPin: PropTypes.bool,
  updateBookmarkList: PropTypes.func,
  isFromBookmarks: PropTypes.bool,
  bookmarkContent: PropTypes.object,
  isVerticalSplit: PropTypes.bool,
  currentTopicId: PropTypes.string,
  forumName: PropTypes.string,
  onCardClick: PropTypes.func,
  removeBookMark: PropTypes.func
};
